/**
 * Principal Session Review Center — two-pane workflow.
 *
 * Left  : queue of reports (newest first, filter pills, status badges)
 * Right : selected report rendered in the same Aanya structure parents/observers see,
 *         with an AI 5-line summary at the top, feedback fields for both parent and
 *         observer, and Approve / Reject + Raise-Ticket actions at the bottom.
 *
 * Approving flips the linked parent_insights to ready_for_parent=true so the
 * parent dashboard reveals the report. Rejecting hides it and sends feedback
 * back to the observer.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, XCircle, AlertTriangle, Clock, RefreshCw, FileText,
  Sparkles, MessageSquare, LifeBuoy, Search, ChevronRight, User2, Calendar,
  Phone, Mail, Users, MapPin, Volume2, Pencil, Copy, Download, ChevronDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { principalNavItems } from '../config/principalNav';
import TextReport from '../components/TextReport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ───────────────────────── helpers (module scope keeps ESLint immutability happy)
const STATUS_LABEL = {
  pending_review: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_PILL = {
  pending_review: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};

const STATUS_ICON = {
  pending_review: Clock,
  approved: CheckCircle2,
  rejected: XCircle,
};

const RATING_BADGE = {
  excellent: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  good: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  fair: 'bg-amber-100 text-amber-800 border-amber-200',
  needs_work: 'bg-rose-100 text-rose-700 border-rose-200',
};

const fmtDateTime = (raw) => {
  if (!raw) return '—';
  const d = typeof raw === 'string' ? new Date(raw) : raw;
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const fmtDate = (raw) => {
  if (!raw) return '—';
  const d = typeof raw === 'string' ? new Date(raw) : raw;
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const ratingKey = (r) => (r || '').toString().toLowerCase().replace(/\s+/g, '_');

const PrincipalReviewCenter = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [token, setToken] = useState('');
  const [principalName, setPrincipalName] = useState('Principal');
  const [queue, setQueue] = useState([]);
  const [counts, setCounts] = useState({ pending_review: 0, approved: 0, rejected: 0, all: 0 });
  const [filter, setFilter] = useState('pending_review');
  const [search, setSearch] = useState('');
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [selectedId, setSelectedId] = useState(searchParams.get('id') || null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [parentFeedback, setParentFeedback] = useState('');
  const [observerFeedback, setObserverFeedback] = useState('');
  const [annotations, setAnnotations] = useState({});
  const [acting, setActing] = useState(false);

  // ───────── auth bootstrap
  useEffect(() => {
    const t = localStorage.getItem('principal_token');
    const n = localStorage.getItem('principal_name');
    if (!t) { navigate('/login'); return; }
    setToken(t);
    setPrincipalName(n || 'Principal');
  }, [navigate]);

  // ───────── queue loader
  const loadQueue = useCallback(async () => {
    if (!token) return;
    setLoadingQueue(true);
    try {
      const params = new URLSearchParams({ token, limit: '200' });
      if (filter !== 'all') params.set('status', filter);
      const { data } = await axios.get(`${BACKEND_URL}/api/principal/review/queue?${params}`);
      setQueue(data.reports || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast({ title: 'Could not load queue', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoadingQueue(false);
    }
  }, [token, filter, toast]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Auto-select first row when queue arrives & nothing is selected.
  useEffect(() => {
    if (!selectedId && queue.length > 0) {
      const id = queue[0].id;
      setSelectedId(id);
    }
  }, [queue, selectedId]);

  // ───────── detail loader
  const loadDetail = useCallback(async (id) => {
    setLoadingDetail(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/principal/review/${id}?token=${token}`);
      setDetail(data);
      setParentFeedback(data.principal_feedback_for_parent || '');
      setObserverFeedback(data.principal_feedback_for_observer || '');
      setAnnotations(data.principal_annotations || {});
    } catch (e) {
      toast({
        title: 'Could not load report',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoadingDetail(false);
    }
  }, [token, toast]);

  useEffect(() => {
    if (!token || !selectedId) {
      return;
    }
    setSearchParams({ id: selectedId }, { replace: true });
    loadDetail(selectedId);
  }, [selectedId, token, setSearchParams, loadDetail]);

  const visibleQueue = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return queue;
    return queue.filter((r) =>
      (r.child_name || '').toLowerCase().includes(q)
      || (r.observer_name || '').toLowerCase().includes(q)
      || (r.id || '').toLowerCase().includes(q),
    );
  }, [queue, search]);

  // ───────── actions
  const doAction = async (kind) => {
    if (!detail) return;
    setActing(true);
    try {
      const url = `${BACKEND_URL}/api/principal/review/${detail.id}/${kind}?token=${token}`;
      await axios.post(url, {
        parent_feedback: parentFeedback,
        observer_feedback: observerFeedback,
        reason: kind === 'reject' ? observerFeedback : '',
        annotations,
      });
      toast({
        title: kind === 'approve' ? 'Report approved' : 'Report rejected',
        description: kind === 'approve'
          ? 'Parent can now see this report on their dashboard.'
          : 'Feedback has been sent back to the observer.',
      });
      // Refresh
      await loadQueue();
      const next = queue.find((r) => r.id !== detail.id);
      setSelectedId(next?.id || null);
    } catch (e) {
      toast({
        title: 'Action failed',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setActing(false);
    }
  };

  const raiseTicket = () => {
    if (!detail) return;
    const subject = encodeURIComponent(`Report review issue · ${detail.child_name || 'child'} · ${detail.id}`);
    navigate(`/principal/support?subject=${subject}&report_id=${detail.id}`);
  };

  // ───────── render
  return (
    <ResponsiveLayout
      navItems={principalNavItems}
      userRole="principal"
      userName={principalName}
      onLogout={() => { localStorage.clear(); navigate('/login'); }}
    >
      <div className="px-4 lg:px-8 py-6 max-w-[1600px] mx-auto" data-testid="principal-review-center">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-sanjaya-navy flex items-center gap-2 sm:gap-3">
              <FileText className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-sanjaya-navy" />
              Session Review Center
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">
              Approve or reject observer-uploaded session reports before they reach parents.
            </p>
          </div>
          <Button onClick={loadQueue} variant="outline" size="sm" data-testid="review-refresh-btn" className="border-sanjaya-navy/30 text-sanjaya-navy self-start sm:self-auto">
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingQueue ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Status pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {['pending_review', 'approved', 'rejected', 'all'].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`review-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                filter === k
                  ? 'bg-sanjaya-navy text-white border-sanjaya-navy'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {k === 'all' ? 'All' : STATUS_LABEL[k]}
              <span className="ml-1.5 opacity-75">{counts[k] || 0}</span>
            </button>
          ))}
        </div>

        {/* Two-pane layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
          {/* ─── LEFT: queue ─── */}
          <aside className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col"
                 data-testid="review-queue">
            <div className="p-3 border-b border-slate-100">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by child, observer, or report ID…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="review-queue-search"
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sanjaya-navy/20 focus:border-sanjaya-navy"
                />
              </div>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
              {loadingQueue && (
                <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
              )}
              {!loadingQueue && visibleQueue.length === 0 && (
                <div className="p-8 text-center text-slate-400 text-sm">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No reports in this view.
                </div>
              )}
              {!loadingQueue && visibleQueue.map((r) => {
                const Icon = STATUS_ICON[r.review_status] || Clock;
                const active = r.id === selectedId;
                return (
                  <button
                    key={r.id}
                    onClick={() => setSelectedId(r.id)}
                    data-testid={`review-queue-row-${r.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 transition flex items-start gap-3 ${
                      active ? 'bg-sanjaya-navy/5 border-l-4 border-l-sanjaya-orange'
                             : 'hover:bg-slate-50 border-l-4 border-l-transparent'
                    }`}
                  >
                    <div className={`mt-0.5 rounded-full p-1.5 ${STATUS_PILL[r.review_status] || ''} border`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-slate-900 truncate">{r.child_name}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_PILL[r.review_status]}`}>
                          {STATUS_LABEL[r.review_status]}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        <User2 className="w-3 h-3 inline mr-1" />
                        {r.observer_name}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {fmtDateTime(r.generated_at)}
                      </p>
                    </div>
                    {active && <ChevronRight className="w-4 h-4 text-sanjaya-orange mt-1" />}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* ─── RIGHT: detail ─── */}
          <main className="bg-white rounded-xl border border-slate-200 min-h-[600px] flex flex-col"
                data-testid="review-detail">
            {!selectedId && (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-12 text-center">
                <FileText className="w-12 h-12 mb-3 opacity-50" />
                <p>Select a report from the queue to review it.</p>
              </div>
            )}

            {selectedId && loadingDetail && (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading report…
              </div>
            )}

            {selectedId && !loadingDetail && detail && (
              <ReportPane
                report={detail}
                token={token}
                parentFeedback={parentFeedback}
                setParentFeedback={setParentFeedback}
                observerFeedback={observerFeedback}
                setObserverFeedback={setObserverFeedback}
                annotations={annotations}
                setAnnotation={(key, val) => setAnnotations((prev) => ({ ...prev, [key]: val }))}
                onApprove={() => doAction('approve')}
                onReject={() => doAction('reject')}
                acting={acting}
                onRaiseTicket={raiseTicket}
              />
            )}
          </main>
        </div>
      </div>
    </ResponsiveLayout>
  );
};

// ───────── inner detail pane (kept in same file for cohesion)
const ReportPane = ({
  report, token,
  parentFeedback, setParentFeedback, observerFeedback, setObserverFeedback,
  annotations, setAnnotation,
  onApprove, onReject, acting, onRaiseTicket,
}) => {
  const rd = report.report_data || {};
  const status = report.review_status || 'pending_review';
  const StatusIcon = STATUS_ICON[status] || Clock;
  const isFinalised = status === 'approved' || status === 'rejected';

  return (
    <div className="flex flex-col h-full" data-testid="review-detail-pane">
      {/* Header row */}
      <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 font-medium">
            {report.source || 'report'} · {report.id}
          </p>
          <h2 className="text-xl font-bold font-heading text-sanjaya-navy mt-1" data-testid="review-child-name">
            {rd.child_name || report.child_name}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            <span className="font-medium text-slate-700">Observer:</span> {report.observer_name}
            <span className="mx-2 text-slate-300">·</span>
            <span className="font-medium text-slate-700">Date:</span> {fmtDate(rd.date || report.report_date)}
          </p>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${STATUS_PILL[status]}`}>
          <StatusIcon className="w-3.5 h-3.5" />
          {STATUS_LABEL[status]}
        </div>
      </div>

      {/* Body — scrollable */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        {/* Connect with Parent panel */}
        {report.parent_contact && (
          <ParentContactCard contact={report.parent_contact} childName={rd.child_name || report.child_name} />
        )}

        {/* ── Audio Player ── */}
        <ReportAudioPlayer report={report} token={token} />

        {/* AI Summary */}
        <Card className="border-sanjaya-orange/30 bg-gradient-to-br from-sanjaya-orange/10 via-sanjaya-orange/5 to-white"
              data-testid="review-ai-summary">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-sanjaya-orange" />
              <h3 className="text-sm font-semibold text-sanjaya-navy uppercase tracking-wide font-heading">
                AI Summary
              </h3>
            </div>
            {report.ai_summary_5_lines ? (
              <ul className="space-y-2 text-slate-700 text-sm leading-relaxed">
                {report.ai_summary_5_lines.split('\n').filter(Boolean).map((line, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-sanjaya-orange/20 text-sanjaya-orange font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-400 text-sm italic">
                AI summary will appear here once generated — this happens automatically when a report has content.
              </p>
            )}
          </CardContent>
        </Card>

        {/* ── Annotation toolbar hint ── */}
        {!isFinalised && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
            <span className="text-base">🖊️</span>
            <span>
              Use the <strong>highlighter</strong> and <strong>📌 sticky note</strong> buttons on each section below to annotate this report.
              Your marks will appear as <em>Principal&apos;s Remarks</em> on the parent&apos;s report.
            </span>
          </div>
        )}

        {/* Day's Insights header (no annotation) */}
        <Section title="Day's Insights">
          <KV label="Child's Name" value={rd.child_name || report.child_name} />
          <KV label="Date" value={fmtDate(rd.date || report.report_date)} />
          {!rd.report_text && <KV label="Curiosity Seed Explored" value={rd.curiosity_seed} />}
        </Section>

        {/* Text-first (v2) report */}
        {rd.report_text && (
          <TextReport text={rd.report_text} title="Daily Insights Report" />
        )}

        {/* Growth Metrics */}
        {Array.isArray(rd.growth_metrics) && rd.growth_metrics.length > 0 && (
          <AnnotatableSection
            sectionKey="growth_metrics"
            sectionTitle="Growth Metrics & Observations"
            annotation={annotations.growth_metrics}
            onAnnotate={(v) => setAnnotation('growth_metrics', v)}
            disabled={isFinalised}
          >
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Growth Area</th>
                    <th className="text-left px-3 py-2 font-medium">Rating</th>
                    <th className="text-left px-3 py-2 font-medium">Observation</th>
                  </tr>
                </thead>
                <tbody>
                  {rd.growth_metrics.map((m, i) => (
                    <tr key={i} className="border-t border-slate-100 align-top">
                      <td className="px-3 py-2 font-medium text-slate-800">{m.area}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block text-xs font-medium px-2 py-1 rounded border ${RATING_BADGE[ratingKey(m.rating)] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                          {(m.rating || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{m.observation || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnnotatableSection>
        )}

        {rd.curiosity_response_index && (
          <AnnotatableSection
            sectionKey="curiosity_response_index"
            sectionTitle="Curiosity Response Index"
            annotation={annotations.curiosity_response_index}
            onAnnotate={(v) => setAnnotation('curiosity_response_index', v)}
            disabled={isFinalised}
          >
            <p className="text-lg font-semibold text-slate-800 mb-1">
              Score: {rd.curiosity_response_index.score ?? '—'} / {rd.curiosity_response_index.max_score ?? 10}
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">{rd.curiosity_response_index.analysis}</p>
          </AnnotatableSection>
        )}

        {rd.disposition && (
          <AnnotatableSection
            sectionKey="disposition"
            sectionTitle="Disposition During the Session"
            annotation={annotations.disposition}
            onAnnotate={(v) => setAnnotation('disposition', v)}
            disabled={isFinalised}
          >
            {rd.engagement_score && (
              <p className="text-xs font-semibold text-orange-700 mb-1">
                Engagement: {rd.engagement_score.score}/100 · {rd.engagement_score.label}
              </p>
            )}
            <p className="text-sm text-slate-600 leading-relaxed">{rd.disposition}</p>
          </AnnotatableSection>
        )}

        {rd.communication_skills && (
          <AnnotatableSection
            sectionKey="communication_skills"
            sectionTitle="Communication Skills & Thought Clarity"
            annotation={annotations.communication_skills}
            onAnnotate={(v) => setAnnotation('communication_skills', v)}
            disabled={isFinalised}
          >
            <p className="text-sm text-slate-600 leading-relaxed">{rd.communication_skills}</p>
          </AnnotatableSection>
        )}

        {rd.overall_growth_score && (
          <AnnotatableSection
            sectionKey="overall_growth_score"
            sectionTitle="Overall Growth Score"
            annotation={annotations.overall_growth_score}
            onAnnotate={(v) => setAnnotation('overall_growth_score', v)}
            disabled={isFinalised}
          >
            <p className="text-sm font-semibold text-slate-800 mb-1">
              {rd.overall_growth_score.rating} — {rd.overall_growth_score.areas_active}/{rd.overall_growth_score.total_areas} Areas Active
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">{rd.overall_growth_score.summary}</p>
          </AnnotatableSection>
        )}

        {rd.note_for_parent && (
          <AnnotatableSection
            sectionKey="note_for_parent"
            sectionTitle="Note for Parent"
            annotation={annotations.note_for_parent}
            onAnnotate={(v) => setAnnotation('note_for_parent', v)}
            disabled={isFinalised}
          >
            <p className="text-sm text-slate-600 leading-relaxed italic">{rd.note_for_parent}</p>
          </AnnotatableSection>
        )}

        {/* Full Transcript */}
        {report.transcript && (
          <TranscriptPanel report={report} token={token} />
        )}

        {/* Feedback fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5 mb-2">
              <MessageSquare className="w-3.5 h-3.5" /> Feedback for Parent
            </label>
            <Textarea
              value={parentFeedback}
              onChange={(e) => setParentFeedback(e.target.value)}
              placeholder="A short note that will appear on the parent's dashboard alongside this report…"
              rows={4}
              data-testid="review-parent-feedback"
              disabled={isFinalised}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5 mb-2">
              <MessageSquare className="w-3.5 h-3.5" /> Feedback for Observer
            </label>
            <Textarea
              value={observerFeedback}
              onChange={(e) => setObserverFeedback(e.target.value)}
              placeholder="Coaching notes for the observer (mandatory when rejecting)…"
              rows={4}
              data-testid="review-observer-feedback"
              disabled={isFinalised}
            />
          </div>
        </div>

        {isFinalised && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
            This report was {STATUS_LABEL[status].toLowerCase()} on{' '}
            <span className="font-medium">{fmtDateTime(report.reviewed_at)}</span>. Feedback fields are locked.
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRaiseTicket}
          data-testid="review-raise-ticket-btn"
          className="text-slate-600 hover:text-slate-900"
        >
          <LifeBuoy className="w-4 h-4 mr-1.5" /> Raise ticket to admin
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onReject}
            disabled={acting || isFinalised}
            data-testid="review-reject-btn"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
          >
            <XCircle className="w-4 h-4 mr-1.5" /> Reject
          </Button>
          <Button
            onClick={onApprove}
            disabled={acting || isFinalised}
            data-testid="review-approve-btn"
            className="bg-sanjaya-teal hover:bg-sanjaya-teal/90 text-white"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve & Send to Parent
          </Button>
        </div>
      </div>
    </div>
  );
};

// ──────── Full transcript panel with copy + download
const TranscriptPanel = ({ report, token }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = report.transcript || '';
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;

  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const downloadTxt = () => {
    const blob = new Blob(
      [`Report ID : ${report.id}\nChild     : ${report.child_name}\nDate      : ${(report.created_at || '').slice(0, 10)}\n${'='.repeat(60)}\n\n${text}\n`],
      { type: 'text/plain' }
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `transcript_${report.id}.txt`;
    a.click();
  };

  return (
    <Card className="border-blue-200" data-testid="principal-call-transcript">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-blue-600" />
          <h3 className="text-sm font-semibold text-sanjaya-navy uppercase tracking-wide font-heading">
            Session Transcript
          </h3>
          <span className="ml-1 text-[11px] text-slate-400">{wordCount} words</span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={copy}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
              title="Copy transcript"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={downloadTxt}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
              title="Download as .txt"
            >
              <Download className="w-3.5 h-3.5" />
              .txt
            </button>
          </div>
        </div>
        <div className={`bg-slate-50 rounded-lg p-4 overflow-y-auto transition-all ${expanded ? 'max-h-[600px]' : 'max-h-64'}`}>
          <p className="text-slate-700 whitespace-pre-wrap text-sm leading-relaxed font-mono">{text}</p>
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          {expanded ? 'Collapse transcript' : 'Expand full transcript'}
        </button>
      </CardContent>
    </Card>
  );
};

// ──────── Audio player for the session recording
const SOURCE_LABELS = {
  tata_sync_pipeline: 'Tata Call Recording',
  video_call: 'Video Call Audio',
  observer_call_report: 'Uploaded Audio',
};

const ReportAudioPlayer = ({ report, token }) => {
  let audioUrl = null;
  let label = 'Session Audio';

  if (report.recording_url) {
    audioUrl = report.recording_url;
    label = SOURCE_LABELS[report.source] || 'Call Recording';
  } else if (report.audio_filename) {
    audioUrl = `${BACKEND_URL}/api/principal/report-audio/${report.id}?token=${encodeURIComponent(token)}`;
    label = SOURCE_LABELS[report.source] || 'Uploaded Audio';
  }

  if (!audioUrl) return null;

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid="review-audio-player">
      <div className="flex items-center gap-2 mb-3">
        <Volume2 className="w-4 h-4 text-sanjaya-navy" />
        <h3 className="text-sm font-semibold text-sanjaya-navy uppercase tracking-wide font-heading">
          Session Audio
        </h3>
        <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-sanjaya-navy/10 text-sanjaya-navy border border-sanjaya-navy/20">
          {label}
        </span>
      </div>
      <audio controls className="w-full rounded-lg" preload="metadata">
        <source src={audioUrl} />
      </audio>
      <p className="text-xs text-slate-400 mt-2">Audio used to generate this report.</p>
    </div>
  );
};

// ──────── Highlight colours available to the principal
const HIGHLIGHT_COLORS = {
  yellow: { bg: 'bg-yellow-50',   border: 'border-l-[3px] border-yellow-400', dot: 'bg-yellow-400',   ring: 'ring-yellow-500',   label: 'Yellow'  },
  green:  { bg: 'bg-emerald-50',  border: 'border-l-[3px] border-emerald-400',dot: 'bg-emerald-400',  ring: 'ring-emerald-500',  label: 'Green'   },
  pink:   { bg: 'bg-pink-50',     border: 'border-l-[3px] border-pink-400',   dot: 'bg-pink-400',     ring: 'ring-pink-500',     label: 'Pink'    },
  blue:   { bg: 'bg-blue-50',     border: 'border-l-[3px] border-blue-400',   dot: 'bg-blue-400',     ring: 'ring-blue-500',     label: 'Blue'    },
};

// ──────── Annotatable section: highlighter pen + sticky note per section
const AnnotatableSection = ({ sectionKey, sectionTitle, annotation, onAnnotate, disabled, children }) => {
  const [noteOpen, setNoteOpen] = React.useState(!!(annotation?.note));
  const color = annotation?.highlight_color || null;
  const note  = annotation?.note || '';
  const cs    = color ? HIGHLIGHT_COLORS[color] : null;

  const setHighlight = (c) => {
    const next = color === c ? null : c;
    onAnnotate({ ...(annotation || {}), section_title: sectionTitle, highlight_color: next });
  };
  const setNote = (v) => {
    onAnnotate({ ...(annotation || {}), section_title: sectionTitle, note: v });
  };

  return (
    <div className={`rounded-r-lg transition-all pl-3 ${cs ? `${cs.bg} ${cs.border}` : 'border-l-[3px] border-transparent'}`}>
      {/* Section heading row */}
      <div className="flex items-center gap-2 mb-2">
        {!cs && <div className="w-2 h-2 rounded-sm bg-sanjaya-navy" />}
        <h3 className="text-sm font-bold text-sanjaya-navy uppercase tracking-wide font-heading">{sectionTitle}</h3>

        {/* Annotation controls */}
        <div className="ml-auto flex items-center gap-1.5">
          {!disabled && (
            <>
              {/* Highlighter dots */}
              {Object.entries(HIGHLIGHT_COLORS).map(([key, s]) => (
                <button
                  key={key}
                  title={`Highlight ${s.label}`}
                  onClick={() => setHighlight(key)}
                  className={`w-4 h-4 rounded-full transition-transform ${s.dot} ${
                    color === key
                      ? `ring-2 ring-offset-1 ${s.ring} scale-125`
                      : 'hover:scale-110 opacity-70 hover:opacity-100'
                  }`}
                />
              ))}
              {/* Sticky note toggle */}
              <button
                onClick={() => setNoteOpen((o) => !o)}
                className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition ml-1 ${
                  note || noteOpen
                    ? 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                    : 'text-slate-400 border-slate-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200'
                }`}
              >
                📌 {note ? 'Edit note' : 'Sticky note'}
              </button>
            </>
          )}
          {disabled && (color || note) && (
            <span className="flex items-center gap-1 text-[10px] text-slate-500">
              {color && <span className={`inline-block w-3 h-3 rounded-full ${HIGHLIGHT_COLORS[color]?.dot}`} />}
              {note && '📌'}
            </span>
          )}
        </div>
      </div>

      {/* Section content */}
      <div className="border-t border-slate-200 pt-3">
        {children}

        {/* Sticky note area */}
        {(noteOpen || (note && disabled)) && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-300 rounded-xl shadow-sm">
            <span className="text-xl leading-none mt-0.5 select-none">📌</span>
            <div className="flex-1 min-w-0">
              {disabled ? (
                <p className="text-sm text-yellow-900 leading-relaxed">{note}</p>
              ) : (
                <>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Type your sticky note here…"
                    className="w-full bg-transparent text-sm text-yellow-900 resize-none outline-none placeholder-yellow-600/50 leading-relaxed"
                    rows={2}
                  />
                  {note && (
                    <button
                      onClick={() => { setNote(''); setNoteOpen(false); }}
                      className="text-[11px] text-yellow-600 hover:text-red-500 mt-0.5"
                    >
                      Remove note
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Plain section (no annotation) — used for Day's Insights header
const Section = ({ title, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <div className="w-2 h-2 rounded-sm bg-sanjaya-navy" />
      <h3 className="text-sm font-bold text-sanjaya-navy uppercase tracking-wide font-heading">{title}</h3>
    </div>
    <div className="border-t border-slate-200 pt-3 pl-1">{children}</div>
  </div>
);

const KV = ({ label, value }) => (
  <p className="text-sm text-slate-700 mb-1.5">
    <span className="font-semibold text-slate-900">{label}: </span>
    {value || '—'}
  </p>
);

// ────────────────────────────── Parent Contact card
const toWaLink = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return null;
  // Add country code for 10-digit Indian numbers; otherwise pass through.
  const e164 = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${e164}`;
};

const ParentContactCard = ({ contact, childName }) => {
  if (!contact) return null;
  const primaryPhone = contact.phone || contact.mother_phone || contact.father_phone;
  const waLink = toWaLink(primaryPhone);
  const buildWaMessage = () => {
    const greeting = `Hi ${contact.name || 'there'}, this is the principal regarding ${childName || 'your child'}'s session today.`;
    return encodeURIComponent(greeting);
  };

  return (
    <Card className="border-sanjaya-navy/20 bg-sanjaya-navy/5" data-testid="review-parent-contact">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-sanjaya-navy" />
            <h3 className="text-sm font-semibold text-sanjaya-navy uppercase tracking-wide font-heading">
              Connect with Parent
            </h3>
          </div>
          <span className="text-xs text-sanjaya-navy/70">
            Reach the family before approving — useful for sensitive observations.
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Primary parent</p>
            <p className="font-semibold text-slate-900">{contact.name || '—'}</p>
            {contact.email && (
              <p className="text-sm text-slate-700 mt-0.5 flex items-center gap-1.5 break-all">
                <Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" /> {contact.email}
              </p>
            )}
            {primaryPhone && (
              <p className="text-sm text-slate-700 mt-0.5 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-slate-500 shrink-0" /> {primaryPhone}
              </p>
            )}
          </div>
          <div>
            {contact.father_name && (
              <p className="text-sm text-slate-700">
                <span className="text-xs uppercase tracking-wide text-slate-500 font-medium block">Father</span>
                {contact.father_name}
                {contact.father_phone && (
                  <span className="block text-xs text-slate-500 mt-0.5">{contact.father_phone}</span>
                )}
              </p>
            )}
            {contact.mother_name && (
              <p className="text-sm text-slate-700 mt-2">
                <span className="text-xs uppercase tracking-wide text-slate-500 font-medium block">Mother</span>
                {contact.mother_name}
                {contact.mother_phone && (
                  <span className="block text-xs text-slate-500 mt-0.5">{contact.mother_phone}</span>
                )}
              </p>
            )}
          </div>
        </div>

        {contact.address && (
          <p className="text-xs text-slate-600 flex items-start gap-1.5 mb-3 leading-snug">
            <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
            <span>{contact.address}</span>
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {primaryPhone && (
            <a href={`tel:${primaryPhone}`} data-testid="review-parent-call">
              <Button variant="outline" size="sm" className="border-sanjaya-navy/30 text-sanjaya-navy hover:bg-sanjaya-navy/10">
                <Phone className="w-4 h-4 mr-1.5" /> Call
              </Button>
            </a>
          )}
          {waLink && (
            <a href={`${waLink}?text=${buildWaMessage()}`} target="_blank" rel="noopener noreferrer" data-testid="review-parent-whatsapp">
              <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <MessageSquare className="w-4 h-4 mr-1.5" /> WhatsApp
              </Button>
            </a>
          )}
          {contact.email && (
            <a
              href={`mailto:${contact.email}?subject=${encodeURIComponent(
                `About ${childName || 'your child'}'s session`,
              )}`}
              data-testid="review-parent-email"
            >
              <Button variant="outline" size="sm" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                <Mail className="w-4 h-4 mr-1.5" /> Email
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PrincipalReviewCenter;
